import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox.jsx";
import {
  BASIC_DIALOG_ACTION_CLASS,
  BasicDialogFrame,
} from "@/components/ui/BasicDialog.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import UploadDropzone from "@/components/upload/UploadDropzone.jsx";
import SelectCountrySADC from "@/components/fields/SelectCountrySADC.jsx";
import InputPhoneSADC from "@/components/auth/InputPhoneSADC.jsx";
import { useAuth } from "@/contexts/AuthContext";
import { loadProgress, saveProgress } from "@/lib/persistStep";
import { writeLastAuthedPath } from "@/lib/authedPathCache";
import { writeLastRole } from "@/lib/roleCache";
import {
  createPharmacyInviteSignInUrl,
  derivePharmacyOnboardingTruth,
  getActivePharmacyMemberAccess,
  resolvePersistedPharmacyAccess,
  savePharmacyOnboardingTruth,
} from "@/services/pharmacyLaneService.js";
import {
  getPharmacyStripeConnectStatus,
  startPharmacyStripeConnect,
} from "@/services/stripeConnectService.js";
import {
  checkStagingAccess,
  isStagingRuntimeHost,
  normalizeStagingEmail,
  readCachedStagingAccess,
} from "@/services/stagingAccessService.js";
import supabase from "@/services/supabaseClient.js";
import { seedStagingPharmacyDemoData } from "@/services/pharmacyDemoSeedService.js";
import { persistPharmacyOnboardingIdentity } from "@/services/pharmacyOnboardingIdentityService.js";
import { writeOnboardingEvent, writeUserActivityEvent } from "@/services/userTruthTracking.js";

const LS_KEY = "onb_pharmacy_v1";
const PHARMACY_ORIENTATION_SEEN_KEY = "afrimed:pharmacy-orientation-seen";
const DASHBOARD_PATH = "/pharmacy/dashboard";
const TERTIARY_INFO_PANEL_CLASS =
  "rounded-[12px] border border-[rgba(var(--afrimed-tertiary),0.28)] bg-[rgba(var(--afrimed-tertiary),0.08)] p-4";
const MEMBER_ONLY_OWNER_ERROR =
  "This account is linked as a pharmacy branch member. Sign in with the branch owner account to manage onboarding or payouts.";
const STAGING_TEST_EMAIL_DOMAINS = ["afrimed.test"];

const DEFAULT_STATE = {
  pharmacyName: "",
  tradingName: "",
  branchType: "community",
  licenseNumber: "",
  registrationAuthority: "",
  address1: "",
  address2: "",
  city: "",
  region: "",
  postalCode: "",
  country: "",
  latitude: "",
  longitude: "",
  geodataSource: "",
  geodataCapturedAt: "",
  adminName: "",
  firstName: "",
  lastName: "",
  adminEmail: "",
  adminPhone: "",
  adminPhoneCc: "+267",
  whatsappPhone: "",
  whatsappPhoneCc: "+267",
  deliveryEnabled: true,
  pickupEnabled: true,
  verifiedAddressOnly: true,
  deliveryRadiusKm: "15",
  deliveryBaseFee: "35",
  averageFulfilment: "45",
  sponsorFriendly: true,
  inventoryManager: "",
  financeLead: "",
  deliveryLead: "",
  invitedUsers: "",
  staffCsvNames: [],
  staffMembers: [],
  inventoryCsvNames: [],
  inventoryItems: [],
  verificationDeferred: false,
  verificationDeferredAt: "",
  verificationDeferredBy: "",
  verificationStatus: "pending",
  uploadNames: {
    pharmacyLicense: [],
    pharmacistRegistration: [],
    proofOfAddress: [],
    deliveryPermit: [],
  },
};

function wait(ms = 0) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function confirmPersistedPharmacyOwnerProfile(userId = "") {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return false;

  for (let attempt = 0; attempt < 5; attempt += 1) {
    const { data, error } = await supabase
      .from("pharmacy_connect_profiles")
      .select("user_id")
      .eq("user_id", normalizedUserId)
      .maybeSingle();

    if (error) throw error;
    if (String(data?.user_id || "").trim() === normalizedUserId) return true;
    await wait(200);
  }

  return false;
}

function parseCsvLine(line = "") {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
}

function normalizeHeader(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function parseCoordinateValue(value) {
  const parsed = Number(String(value ?? "").trim());
  return Number.isFinite(parsed) ? Number(parsed.toFixed(6)) : null;
}

async function parseStaffCsv(file) {
  const text = await file.text();
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length <= 1) return [];

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  return lines.slice(1).map((line, index) => {
    const cells = parseCsvLine(line);
    const row = headers.reduce((acc, header, cellIndex) => {
      acc[header] = String(cells[cellIndex] || "").trim();
      return acc;
    }, {});

    return {
      id: `${row.email || row.phone || row.full_name || row.name || "staff"}-${index}`,
      fullName: row.full_name || row.name || [row.first_name, row.last_name].filter(Boolean).join(" ").trim(),
      email: row.email || "",
      phone: row.phone || row.mobile || "",
      dob: row.dob || row.date_of_birth || "",
      role: row.role || row.job_title || row.position || "",
      branch: row.branch || row.branch_name || row.location || row.facility_name || "",
      staffNumber: row.staff_number || row.employee_id || row.payroll_id || "",
    };
  }).filter((row) => row.fullName || row.email || row.phone);
}

async function parseInventoryCsv(file) {
  const text = await file.text();
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length <= 1) return [];

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  return lines
    .slice(1)
    .map((line, index) => {
      const cells = parseCsvLine(line);
      const row = headers.reduce((acc, header, cellIndex) => {
        acc[header] = String(cells[cellIndex] || "").trim();
        return acc;
      }, {});

      return {
        id: `${row.stock_id || row.item_name || row.medicine_name || "item"}-${index}`,
        item: row.item_name || row.medicine_name || row.item || row.product_name || "",
        detail: row.strength || row.pack_size || row.detail || "",
        category: row.category || row.medicine_category || row.classification || "",
        stockId: row.stock_id || row.sku || row.item_code || "",
        currentStock: row.current_stock || row.quantity_on_hand || row.on_hand || "0",
        minimalStock: row.minimal_stock || row.reorder_level || row.low_stock_threshold || "0",
        unitPrice: row.unit_price || row.retail_price || row.price || "",
        supplier: row.supplier || row.supplier_name || row.vendor || "",
        branch: row.branch_name || row.branch || row.location || "",
        batchNumber: row.batch_number || "",
        expiryDate: row.expiry_date || "",
        binLocation: row.bin_location || row.bin || "",
        imageUrl: row.image_url || row.image || row.photo_url || row.picture_url || "",
      };
    })
    .filter((row) => row.item || row.stockId || row.supplier);
}

function inferLead(members = [], matcher) {
  return members.find((member) => matcher(String(member.role || "").toLowerCase()))?.fullName || "";
}

const TEAM_ROLES = [
  {
    title: "Owner / Superintendent pharmacist",
    note: "Full access to onboarding, verification, pricing, payouts, and delivery settings.",
  },
  {
    title: "Dispensing pharmacist",
    note: "Process QR prescriptions, approve substitutions, and mark pickup or delivery ready.",
  },
  {
    title: "Inventory manager",
    note: "Maintain stock counts, low-stock alerts, supplier references, and cold-chain notes.",
  },
  {
    title: "Finance / settlements",
    note: "See medicine revenue, AfriMed™ 10% fee, delivery collections, and payout exports.",
  },
];

function mergeState(saved = {}) {
  return {
    ...DEFAULT_STATE,
    ...saved,
    uploadNames: {
      ...DEFAULT_STATE.uploadNames,
      ...(saved?.uploadNames || {}),
    },
  };
}

function buildConnectMeta(values = {}) {
  const latitude = parseCoordinateValue(values.latitude);
  const longitude = parseCoordinateValue(values.longitude);
  return {
    staging_dashboard_access: values.stagingDashboardAccess === true,
    staging_dashboard_access_at: values.stagingDashboardAccessAt || null,
    staging_onboarding_bypass: values.stagingOnboardingBypass === true,
    branch_profile_incomplete: values.branchProfileIncomplete === true,
    verification_pending: values.verificationStatus === "pending",
    payout_pending: values.payoutPending === true,
    branch_type: String(values.branchType || "").trim(),
    license_number: String(values.licenseNumber || "").trim(),
    registration_authority: String(values.registrationAuthority || "").trim(),
    address_1: String(values.address1 || "").trim(),
    address_2: String(values.address2 || "").trim(),
    city: String(values.city || "").trim(),
    region: String(values.region || "").trim(),
    postal_code: String(values.postalCode || "").trim(),
    country: String(values.country || "").trim(),
    branch_latitude: latitude,
    branch_longitude: longitude,
    branch_geodata_source: String(values.geodataSource || "").trim(),
    branch_geodata_captured_at: String(values.geodataCapturedAt || "").trim(),
    admin_name: String(values.adminName || "").trim(),
    admin_email: String(values.adminEmail || "").trim(),
    admin_phone: [String(values.adminPhoneCc || "").trim(), String(values.adminPhone || "").trim()]
      .filter(Boolean)
      .join(""),
    whatsapp_phone: [
      String(values.whatsappPhoneCc || "").trim(),
      String(values.whatsappPhone || "").trim(),
    ]
      .filter(Boolean)
      .join(""),
    delivery_enabled: Boolean(values.deliveryEnabled),
    pickup_enabled: Boolean(values.pickupEnabled),
    verified_address_only: Boolean(values.verifiedAddressOnly),
    delivery_radius_km: String(values.deliveryRadiusKm || "").trim(),
    delivery_base_fee: String(values.deliveryBaseFee || "").trim(),
    average_fulfilment_minutes: String(values.averageFulfilment || "").trim(),
    sponsor_friendly: Boolean(values.sponsorFriendly),
    inventory_manager: String(values.inventoryManager || "").trim(),
    finance_lead: String(values.financeLead || "").trim(),
    delivery_lead: String(values.deliveryLead || "").trim(),
    staff_members: Array.isArray(values.staffMembers) ? values.staffMembers : [],
    inventory_items: Array.isArray(values.inventoryItems) ? values.inventoryItems : [],
    staff_csv_names: Array.isArray(values.staffCsvNames) ? values.staffCsvNames : [],
    inventory_csv_names: Array.isArray(values.inventoryCsvNames) ? values.inventoryCsvNames : [],
    first_name: String(values.firstName || "").trim(),
    last_name: String(values.lastName || "").trim(),
    invited_staff_count: Array.isArray(values.staffMembers) ? values.staffMembers.length : 0,
    inventory_item_count: Array.isArray(values.inventoryItems) ? values.inventoryItems.length : 0,
    verification_status: values.verificationDeferred
      ? "pending"
      : values.uploadNames?.pharmacyLicense?.length &&
        values.uploadNames?.pharmacistRegistration?.length &&
        values.uploadNames?.proofOfAddress?.length
      ? "submitted"
      : "pending",
    verification_deferred: values.verificationDeferred === true,
    verification_deferred_at: values.verificationDeferredAt || null,
    verification_deferred_by: values.verificationDeferredBy || null,
  };
}

function applyStagingPharmacyPlaceholders(values = {}, { email = "" } = {}) {
  const cleanEmail = normalizeStagingEmail(email || "");
  const pharmacyName = String(values.pharmacyName || "").trim() || "Staging pharmacy branch";
  const responsibleLead = [values.firstName, values.lastName].filter(Boolean).join(" ").trim();
  const storedAdminName = String(values.adminName || "").trim();
  const adminName =
    (storedAdminName && !isEmailHandleLike(storedAdminName) ? storedAdminName : "") ||
    responsibleLead ||
    "Staging pharmacy owner";
  const now = new Date().toISOString();

  return {
    ...values,
    pharmacyName,
    tradingName: String(values.tradingName || "").trim() || pharmacyName,
    branchType: values.branchType || "community",
    country: String(values.country || "").trim() || "South Africa",
    adminName,
    adminEmail: String(values.adminEmail || "").trim() || cleanEmail,
    pickupEnabled: values.pickupEnabled || !values.deliveryEnabled,
    verificationDeferred: true,
    verificationDeferredAt: values.verificationDeferredAt || now,
    verificationDeferredBy: values.verificationDeferredBy || cleanEmail,
    verificationStatus: "pending",
    stagingDashboardAccess: true,
    stagingDashboardAccessAt: now,
    stagingOnboardingBypass: true,
    branchProfileIncomplete: true,
    payoutPending: true,
  };
}

function isStagingPharmacyBypassRuntime() {
  if (typeof window === "undefined") return import.meta.env.DEV === true;
  return isStagingRuntimeHost(window.location.hostname) || import.meta.env.DEV === true;
}

function isStagingSmokeEmail(value = "") {
  const email = normalizeStagingEmail(value);
  if (!email) return false;
  return STAGING_TEST_EMAIL_DOMAINS.some((domain) => email.endsWith(`@${domain}`));
}

function hasStagingSmokeMetadata(metadata = {}) {
  return (
    metadata?.smoke_account === true ||
    metadata?.pharmacy_smoke === true ||
    metadata?.is_demo === true ||
    String(metadata?.account_classification || "").toLowerCase() === "smoke" ||
    String(metadata?.classification || "").toLowerCase() === "smoke"
  );
}

function getPharmacyOrientationKey(userId = "", email = "") {
  const ownerKey = String(userId || email || "session").trim().toLowerCase();
  return `${PHARMACY_ORIENTATION_SEEN_KEY}:${ownerKey || "session"}`;
}

function getFirstNameFromAuth(auth = {}) {
  const isUsableName = (value = "") => {
    const text = String(value || "").trim();
    if (!text) return false;
    if (isEmailHandleLike(text)) return false;
    return /[a-z]/i.test(text);
  };
  const firstName = String(
    auth?.profile?.first_name || auth?.user?.user_metadata?.first_name || "",
  ).trim();
  if (isUsableName(firstName)) return firstName.split(/\s+/)[0] || "";

  const rawName = String(
    auth?.profile?.display_name ||
      auth?.profile?.full_name ||
      auth?.user?.user_metadata?.full_name ||
      auth?.user?.user_metadata?.name ||
      "",
  ).trim();

  if (!isUsableName(rawName)) return "";
  return rawName.split(/\s+/)[0] || "";
}

function isEmailHandleLike(value = "") {
  const text = String(value || "").trim();
  if (!text) return false;
  if (text.includes("@")) return true;
  return /^[a-z0-9._-]+$/i.test(text) && text.includes(".");
}

function getFirstTokenName(value = "") {
  const text = String(value || "").trim();
  if (!text || isEmailHandleLike(text)) return "";
  return text.split(/\s+/)[0] || "";
}

function PharmacyOrientationDialog({ open, firstName = "", onDismiss }) {
  if (!open) return null;

  const title = firstName ? `Welcome ${firstName}` : "Welcome";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/35" onClick={onDismiss} />
      <BasicDialogFrame
        role="dialog"
        aria-modal="true"
        title={title}
        body={
          <div className="space-y-3 text-left">
            <p>Thank you for taking part in AfriMed™ staging.</p>
            <p>
              The next five pharmacy onboarding steps have been opened for testing
              purposes.
            </p>
            <p>
              During staging, you do not need to complete every field or upload
              verification documents.
            </p>
            <p>
              Simply select Continue at the bottom of each step to explore the
              pharmacy onboarding journey.
            </p>
            <p>We look forward to receiving your feedback.</p>
          </div>
        }
        actionsClassName="justify-center"
      >
        <button
          className={`${BASIC_DIALOG_ACTION_CLASS} max-w-[240px] bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))] border-[rgb(var(--afrimed-primary))] shadow-[0_3px_6px_-3px_rgba(29,79,215,0.35)]`}
          onClick={onDismiss}
        >
          Understood
        </button>
      </BasicDialogFrame>
    </div>
  );
}

function useTabletPortraitFooterFallback() {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const query = window.matchMedia(
      "(min-width: 768px) and (max-width: 1180px) and (orientation: portrait)",
    );
    const sync = () => setMatches(query.matches);
    sync();

    if (query.addEventListener) {
      query.addEventListener("change", sync);
      return () => query.removeEventListener("change", sync);
    }

    query.addListener(sync);
    return () => query.removeListener(sync);
  }, []);

  return matches;
}

function Shell({ step, total, onBack, title, subtitle, footer, children }) {
  const useInlineFooter = useTabletPortraitFooterFallback();

  return (
    <div className="h-dvh min-h-0 flex flex-col overflow-hidden">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <div className="min-h-0 flex-1 [&>div]:min-h-full">
        <OnboardingSection
          header={
            <div className="space-y-2">
              <h2 className="text-title-large text-on-surface">{title}</h2>
              <p className="text-body-medium text-on-surface-var">{subtitle}</p>
            </div>
          }
          footer={useInlineFooter ? null : footer}
          footerTab
          footerHeight={104}
          bodyClassName={useInlineFooter ? "pb-6" : ""}
        >
          {children}
          {useInlineFooter && footer ? (
            <div className="sticky bottom-[calc(env(safe-area-inset-bottom)+176px)] z-30 mx-auto mt-4 w-full max-w-[560px] px-3 py-2 md:px-0">
              <div className="flex flex-col gap-3 items-stretch">{footer}</div>
            </div>
          ) : null}
        </OnboardingSection>
      </div>
    </div>
  );
}

function ToggleCard({ active, title, desc, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "w-full rounded-[12px] px-4 py-3 text-left transition",
        active
          ? "bg-[rgba(29,79,215,0.08)] shadow-[0px_0px_0px_1px_rgba(var(--afrimed-primary),0.9)]"
          : "bg-[rgb(var(--clr-surface))] shadow-[0px_0px_0px_1px_rgba(var(--clr-outline-variant),0.35)]",
      ].join(" ")}
    >
      <div className="text-body-large text-on-surface">{title}</div>
      <p className="mt-1 text-body-small text-on-surface-var">{desc}</p>
    </button>
  );
}

function UploadCard({ title, subtitle, value = [], onFiles, required = false }) {
  return (
    <div className="grid gap-2">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-body-large text-on-surface">
            {title}
            {required ? " *" : ""}
          </div>
          <p className="mt-1 text-body-small text-on-surface-var">{subtitle}</p>
        </div>
        {value.length > 0 ? (
          <span className="mb-0.5 rounded-full bg-[rgba(0,189,196,0.12)] px-2.5 py-1 text-label-small text-[rgb(var(--afrimed-secondary))]">
            {value.length} ready
          </span>
        ) : null}
      </div>

      <div className="mt-2">
        <UploadDropzone
          tone="surface"
          accept=".pdf,.png,.jpg,.jpeg,.webp"
          maxSizeMB={12}
          onFiles={onFiles}
        />
      </div>

      {value.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-2">
          {value.map((name) => (
            <span
              key={name}
              className="rounded-full bg-[rgba(24,50,119,0.06)] px-3 py-1 text-label-small text-on-surface-var"
            >
              {name}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function StepIdentity({
  step,
  total,
  values,
  onChange,
  onBack,
  onContinue,
  canBypassPharmacyOnboardingRequirements = false,
}) {
  const [capturingLocation, setCapturingLocation] = React.useState(false);
  const [locationError, setLocationError] = React.useState("");
  const canContinue =
    canBypassPharmacyOnboardingRequirements ||
    (values.pharmacyName.trim() &&
      values.licenseNumber.trim() &&
      values.registrationAuthority.trim() &&
      values.address1.trim() &&
      values.city.trim() &&
      values.country.trim());

  function handleUseCurrentLocation() {
    if (capturingLocation) return;
    if (!navigator?.geolocation) {
      setLocationError("This browser cannot capture branch coordinates. Enter them manually instead.");
      return;
    }

    setCapturingLocation(true);
    setLocationError("");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        onChange({
          latitude: String(Number(coords.latitude).toFixed(6)),
          longitude: String(Number(coords.longitude).toFixed(6)),
          geodataSource: "browser-geolocation",
          geodataCapturedAt: new Date().toISOString(),
        });
        setCapturingLocation(false);
      },
      (error) => {
        let message = "AfriMed™ could not capture this branch location. Enter coordinates manually instead.";
        if (error?.code === error.PERMISSION_DENIED) {
          message = "Location access was denied. Enter branch coordinates manually or allow location access.";
        } else if (error?.code === error.TIMEOUT) {
          message = "Location capture timed out. Try again or enter the branch coordinates manually.";
        }
        setLocationError(message);
        setCapturingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 300000 },
    );
  }

  return (
    <Shell
      step={step}
      total={total}
      onBack={onBack}
      title="Pharmacy identity"
      subtitle="Set up the legal pharmacy profile first so AfriMed™ can surface signed-up branch availability, medicine pricing, and fulfilment terms cleanly."
      footer={
        <Button fullWidth disabled={!canContinue} onClick={onContinue}>
          Continue
        </Button>
      }
    >
      <div className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField56
            tone="surface"
            label="Firstname"
            value={values.firstName}
            onChange={(v) => onChange({ firstName: v })}
          />
          <TextField56
            tone="surface"
            label="Lastname"
            value={values.lastName}
            onChange={(v) => onChange({ lastName: v })}
          />
        </div>
        <TextField56 tone="surface" label="Pharmacy name*" value={values.pharmacyName} onChange={(v) => onChange({ pharmacyName: v })} />
        <TextField56 tone="surface" label="Trading name" value={values.tradingName} onChange={(v) => onChange({ tradingName: v })} />
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField56 tone="surface" label="License number*" value={values.licenseNumber} onChange={(v) => onChange({ licenseNumber: v })} />
          <TextField56 tone="surface" label="Registration authority*" value={values.registrationAuthority} onChange={(v) => onChange({ registrationAuthority: v })} />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <ToggleCard
            active={values.branchType === "community"}
            title="Community"
            desc="Retail dispensing for walk-in, pickup, and family-funded care."
            onClick={() => onChange({ branchType: "community" })}
          />
          <ToggleCard
            active={values.branchType === "hospital"}
            title="Hospital"
            desc="Institution-linked dispensary with AfriMed™ prescription processing."
            onClick={() => onChange({ branchType: "hospital" })}
          />
          <ToggleCard
            active={values.branchType === "specialty"}
            title="Specialty"
            desc="Higher-value and more tightly managed medicine categories."
            onClick={() => onChange({ branchType: "specialty" })}
          />
        </div>
        <TextField56 tone="surface" label="Address line 1*" value={values.address1} onChange={(v) => onChange({ address1: v })} />
        <TextField56 tone="surface" label="Address line 2" value={values.address2} onChange={(v) => onChange({ address2: v })} />
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField56 tone="surface" label="City*" value={values.city} onChange={(v) => onChange({ city: v })} />
          <TextField56 tone="surface" label="Region / State" value={values.region} onChange={(v) => onChange({ region: v })} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField56 tone="surface" label="Postal code" value={values.postalCode} onChange={(v) => onChange({ postalCode: v })} />
          <SelectCountrySADC tone="surface" label="Country*" value={values.country} onChange={(v) => onChange({ country: v })} />
        </div>
        <div className={TERTIARY_INFO_PANEL_CLASS}>
          <div className="text-title-small text-on-surface">Staging note</div>
          <p className="mt-2 text-body-small text-on-surface-var">
            Invited testers can complete the pharmacy walkthrough without uploading verification documents. Verification is reviewed later before production routing or fulfilment.
          </p>
        </div>
        <div className={TERTIARY_INFO_PANEL_CLASS}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-body-large text-on-surface">Branch map coordinates</div>
              <p className="mt-2 text-body-medium text-on-surface-var">
                These coordinates let AfriMed™ rank signed-up pharmacies by true distance when patients choose where a prescription should be prepared.
              </p>
            </div>
            <Button type="button" variant="tonal" onClick={handleUseCurrentLocation} disabled={capturingLocation}>
              {capturingLocation ? "Capturing location…" : "Use current location"}
            </Button>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <TextField56
              tone="surface"
              label="Latitude"
              value={values.latitude}
              onChange={(v) => onChange({ latitude: v })}
            />
            <TextField56
              tone="surface"
              label="Longitude"
              value={values.longitude}
              onChange={(v) => onChange({ longitude: v })}
            />
          </div>
          <div className="mt-3 text-body-small text-on-surface-var">
            {values.latitude && values.longitude
              ? `Stored branch coordinates: ${values.latitude}, ${values.longitude}${values.geodataSource ? ` • ${values.geodataSource}` : ""}`
              : "Optional but recommended. You can capture this automatically here or paste the branch coordinates manually."}
          </div>
          {locationError ? (
            <div className="mt-3 text-body-small text-[rgb(var(--clr-error))]">{locationError}</div>
          ) : null}
        </div>
      </div>
    </Shell>
  );
}

function StepOperations({
  step,
  total,
  values,
  onChange,
  onBack,
  onContinue,
  canBypassPharmacyOnboardingRequirements = false,
}) {
  const canContinue =
    canBypassPharmacyOnboardingRequirements ||
    (values.adminName.trim() &&
      values.adminEmail.trim() &&
      values.adminPhone.trim() &&
      (values.pickupEnabled || values.deliveryEnabled));

  async function handleInventoryCsv(files = []) {
    const names = Array.isArray(files)
      ? files.map((file) => String(file?.name || "").trim()).filter(Boolean)
      : [];

    if (!files?.length) {
      onChange({ inventoryCsvNames: [], inventoryItems: [] });
      return;
    }

    try {
      const items = await parseInventoryCsv(files[0]);
      onChange({ inventoryCsvNames: names, inventoryItems: items });
    } catch (error) {
      console.error("Error parsing pharmacy inventory CSV", error);
      onChange({ inventoryCsvNames: names, inventoryItems: [] });
    }
  }

  return (
    <Shell
      step={step}
      total={total}
      onBack={onBack}
      title="Operations and patient fulfilment"
      subtitle="This is where we reduce friction for unwell patients: pick-up, delivery, verified-address handling, and a sponsor-friendly payment posture."
      footer={
        <Button fullWidth disabled={!canContinue} onClick={onContinue}>
          Continue
        </Button>
      }
    >
      <div className="grid gap-4">
        <div>
          <div className="text-body-large text-on-surface">Responsible contact</div>
          <div className="mt-4 grid gap-3">
            <TextField56 tone="surface" label="Responsible pharmacist / lead*" value={values.adminName} onChange={(v) => onChange({ adminName: v })} />
            <TextField56 tone="surface" label="Email*" value={values.adminEmail} onChange={(v) => onChange({ adminEmail: v })} />
            <InputPhoneSADC
              label="Phone*"
              value={values.adminPhone}
              cc={values.adminPhoneCc}
              tone="surface"
              onChange={(digits) => onChange({ adminPhone: digits })}
              onChangeCc={(cc) => onChange({ adminPhoneCc: cc })}
            />
            <InputPhoneSADC
              label="WhatsApp / support line"
              value={values.whatsappPhone}
              cc={values.whatsappPhoneCc}
              tone="surface"
              onChange={(digits) => onChange({ whatsappPhone: digits })}
              onChangeCc={(cc) => onChange({ whatsappPhoneCc: cc })}
            />
          </div>
        </div>

        <div>
          <div className="text-body-large text-on-surface">Fulfilment modes</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <ToggleCard
              active={values.pickupEnabled}
              title="In-person pickup"
              desc="Patients can reserve medicines and collect at the branch counter."
              onClick={() => onChange({ pickupEnabled: !values.pickupEnabled })}
            />
            <ToggleCard
              active={values.deliveryEnabled}
              title="Delivery"
              desc="Patients can request delivery to their registered and verified AfriMed™ address."
              onClick={() => onChange({ deliveryEnabled: !values.deliveryEnabled })}
            />
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <TextField56
              tone="surface"
              label="Delivery radius (km)"
              value={values.deliveryRadiusKm}
              onChange={(v) => onChange({ deliveryRadiusKm: v })}
            />
            <TextField56
              tone="surface"
              label="Base delivery fee"
              value={values.deliveryBaseFee}
              onChange={(v) => onChange({ deliveryBaseFee: v })}
            />
            <TextField56
              tone="surface"
              label="Average fulfilment (mins)"
              value={values.averageFulfilment}
              onChange={(v) => onChange({ averageFulfilment: v })}
            />
          </div>

          <div className="mt-4 space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={values.verifiedAddressOnly}
                onChange={(checked) => onChange({ verifiedAddressOnly: checked })}
                className="mt-0.5 shrink-0"
              />
              <button
                type="button"
                className="text-left text-body-medium text-on-surface-var"
                onClick={() => onChange({ verifiedAddressOnly: !values.verifiedAddressOnly })}
              >
                Require delivery requests to use the patient’s registered and verified AfriMed™ address.
              </button>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                checked={values.sponsorFriendly}
                onChange={(checked) => onChange({ sponsorFriendly: checked })}
                className="mt-0.5 shrink-0"
              />
              <button
                type="button"
                className="text-left text-body-medium text-on-surface-var"
                onClick={() => onChange({ sponsorFriendly: !values.sponsorFriendly })}
              >
                Keep sponsor-friendly payment handling available for diaspora and family-funded care.
              </button>
            </div>
          </div>
        </div>

        <div>
          <div className="text-body-large text-on-surface">Inventory import</div>
          <p className="mt-2 text-body-medium text-on-surface-var">
            Bring existing stock, reorder, pricing, and supplier data in from pharmacy software or a spreadsheet so AfriMed™ can seed low-stock warnings and branch availability immediately.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <img src="/icons/list-items/download.svg" alt="" className="h-5 w-5 opacity-80" />
            <a
              href="/data/afrimed-pharmacy-inventory-template.csv"
              download
              className="text-[rgb(var(--afrimed-primary))] no-underline"
            >
              Download pharmacy inventory CSV template
            </a>
          </div>
          <div className="mt-3">
            <UploadDropzone tone="surface" accept=".csv" maxSizeMB={12} onFiles={handleInventoryCsv} />
          </div>
          <p className="mt-2 text-body-small text-on-surface-var">
            Template: item_name, strength, category, stock_id, current_stock, minimal_stock, unit_price, supplier, branch_name, image_url.
          </p>
          {values.inventoryCsvNames.length > 0 ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {values.inventoryItems.length
                ? `Detected ${values.inventoryItems.length} stock rows from ${values.inventoryCsvNames[0]}.`
                : `Uploaded ${values.inventoryCsvNames[0]}, but no usable stock rows were detected.`}
            </div>
          ) : null}
        </div>

        <div className={TERTIARY_INFO_PANEL_CLASS}>
          <div className="text-body-large text-on-surface">AfriMed™ commercial alignment</div>
          <p className="mt-2 text-body-medium text-on-surface-var">
            AfriMed™ takes 10% of the medicine subtotal only. Delivery fees and tax stay outside the commission base so pharmacy settlement remains clear.
          </p>
        </div>
      </div>
    </Shell>
  );
}

function StepVerification({
  step,
  total,
  values,
  onUpload,
  onBack,
  onContinue,
  onDefer,
  stagingBypassEligible = false,
  stagingTesterEmail = "",
}) {
  const hasRequiredUploads =
    values.uploadNames.pharmacyLicense.length > 0 &&
    values.uploadNames.pharmacistRegistration.length > 0 &&
    values.uploadNames.proofOfAddress.length > 0;
  const verificationDeferred = values.verificationDeferred === true;
  const canContinue = hasRequiredUploads || verificationDeferred || stagingBypassEligible;
  const handleContinue = React.useCallback(() => {
    if (stagingBypassEligible && !hasRequiredUploads && !verificationDeferred) {
      onDefer?.();
      return;
    }
    onContinue?.();
  }, [hasRequiredUploads, onContinue, onDefer, stagingBypassEligible, verificationDeferred]);

  return (
    <Shell
      step={step}
      total={total}
      onBack={onBack}
      title="Verification uploads"
      subtitle="Verification is part of onboarding, not an afterthought. Upload the branch evidence AfriMed™ needs before prescriptions start routing here."
      footer={
        <Button fullWidth disabled={!canContinue} onClick={handleContinue}>
          Continue
        </Button>
      }
    >
      <div className="grid gap-4">
        {verificationDeferred ? (
          <div className={TERTIARY_INFO_PANEL_CLASS}>
            <div className="text-title-small text-on-surface">Verification pending</div>
            <p className="mt-2 text-body-small text-on-surface-var">
              Pharmacy verification is marked for later in AfriMed™ staging. Documents are not treated as uploaded or verified.
            </p>
          </div>
        ) : null}
        <UploadCard
          title="Pharmacy license"
          subtitle="Branch or operating license confirming the registered pharmacy."
          value={values.uploadNames.pharmacyLicense}
          required
          onFiles={(files) => onUpload("pharmacyLicense", files)}
        />
        <UploadCard
          title="Responsible pharmacist registration"
          subtitle="Current regulator registration for the pharmacist overseeing AfriMed™ prescriptions."
          value={values.uploadNames.pharmacistRegistration}
          required
          onFiles={(files) => onUpload("pharmacistRegistration", files)}
        />
        <UploadCard
          title="Proof of branch address"
          subtitle="Utility bill, municipal letter, or lease confirming the dispensing location."
          value={values.uploadNames.proofOfAddress}
          required
          onFiles={(files) => onUpload("proofOfAddress", files)}
        />
        <UploadCard
          title="Delivery permit / transport proof"
          subtitle="Optional now, but recommended when delivery or cold-chain fulfilment is enabled."
          value={values.uploadNames.deliveryPermit}
          onFiles={(files) => onUpload("deliveryPermit", files)}
        />
        {stagingBypassEligible && !verificationDeferred ? (
          <div className={TERTIARY_INFO_PANEL_CLASS}>
            <div className="text-title-small text-on-surface">Staging verification bypass</div>
            <p className="mt-2 text-body-small text-on-surface-var">
              This bypass is staging-only for invited AfriMed™ testers. You can continue without uploading pharmacy verification documents now. Production will still require documents, review, and approval before this pharmacy is treated as verified for routing or fulfilment.
            </p>
            {stagingTesterEmail ? (
              <p className="mt-2 text-body-small text-on-surface-var">
                Tester email: {stagingTesterEmail}
              </p>
            ) : null}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button type="button" variant="ghost" className="h-10" onClick={onDefer}>
                Verify later
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Shell>
  );
}

function StepTeam({
  step,
  total,
  values,
  onChange,
  onBack,
  onContinue,
  canBypassPharmacyOnboardingRequirements = false,
}) {
  const canContinue =
    canBypassPharmacyOnboardingRequirements ||
    (values.inventoryManager.trim() && values.financeLead.trim() && values.deliveryLead.trim());

  async function handleStaffCsv(files = []) {
    const names = Array.isArray(files)
      ? files.map((file) => String(file?.name || "").trim()).filter(Boolean)
      : [];

    if (!files?.length) {
      onChange({ staffCsvNames: [], staffMembers: [] });
      return;
    }

    try {
      const members = await parseStaffCsv(files[0]);
      const nextInvitedUsers = members
        .map((member) => member.email)
        .filter(Boolean)
        .join("\n");

      onChange({
        staffCsvNames: names,
        staffMembers: members,
        invitedUsers: nextInvitedUsers || values.invitedUsers,
        inventoryManager:
          values.inventoryManager ||
          inferLead(members, (role) => role.includes("inventory") || role.includes("stock")),
        financeLead:
          values.financeLead ||
          inferLead(members, (role) => role.includes("finance") || role.includes("settlement") || role.includes("accounts")),
        deliveryLead:
          values.deliveryLead ||
          inferLead(members, (role) => role.includes("delivery") || role.includes("dispatch") || role.includes("logistics")),
      });
    } catch (error) {
      console.error("Error parsing pharmacy staff CSV", error);
      onChange({ staffCsvNames: names, staffMembers: [] });
    }
  }

  return (
    <Shell
      step={step}
      total={total}
      onBack={onBack}
      title="Team roles and privilege handoff"
      subtitle="Use the same low-friction admin pattern as institutions: a clear owner, clear operational leads, and scoped privileges for the rest of the team."
      footer={
        <Button fullWidth disabled={!canContinue} onClick={onContinue}>
          Continue
        </Button>
      }
    >
      <div className="grid gap-4">
        <div className="grid gap-3 lg:grid-cols-2">
          {TEAM_ROLES.map((role) => (
            <div
              key={role.title}
              className="rounded-[12px] bg-[rgb(var(--clr-surface))] p-4 shadow-[0px_0px_0px_1px_rgba(var(--clr-outline-variant),0.35)]"
            >
              <div className="text-body-large text-on-surface">{role.title}</div>
              <p className="mt-2 text-body-small text-on-surface-var">{role.note}</p>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-body-large text-on-surface">HR and multi-branch team import</div>
            <p className="mt-2 text-body-small text-on-surface-var">
              If the pharmacy already manages staff in HR, payroll, or accounting software, upload one CSV and let AfriMed™ prefill the team list instead of typing every person by hand.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <img src="/icons/list-items/download.svg" alt="" className="h-5 w-5 opacity-80" />
              <a
                href="/data/afrimed-pharmacy-staff-template.csv"
                download
                className="text-[rgb(var(--afrimed-primary))] no-underline"
              >
                Download pharmacy staff CSV template
              </a>
            </div>
            <div className="mt-3">
              <UploadDropzone tone="surface" accept=".csv" maxSizeMB={10} onFiles={handleStaffCsv} />
            </div>
            <p className="mt-2 text-body-small text-on-surface-var">
              Template: full_name, email, phone, dob, role, branch_name, staff_number.
            </p>
            {values.staffCsvNames.length > 0 ? (
              <div className="mt-3 text-body-medium text-on-surface-var">
                {values.staffMembers.length
                  ? `Detected ${values.staffMembers.length} staff records from ${values.staffCsvNames[0]}.`
                  : `Uploaded ${values.staffCsvNames[0]}, but no usable staff rows were detected.`}
              </div>
            ) : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField56 tone="surface" label="Inventory manager*" value={values.inventoryManager} onChange={(v) => onChange({ inventoryManager: v })} />
            <TextField56 tone="surface" label="Finance lead*" value={values.financeLead} onChange={(v) => onChange({ financeLead: v })} />
          </div>
          <div className="mt-3">
            <TextField56 tone="surface" label="Delivery lead*" value={values.deliveryLead} onChange={(v) => onChange({ deliveryLead: v })} />
          </div>
          <div className="mt-3">
            <label className="mb-2 block text-label-medium text-on-surface-var">Invite team users</label>
            <textarea
              value={values.invitedUsers}
              onChange={(e) => onChange({ invitedUsers: e.target.value })}
              placeholder="Add one email per line for dispensing pharmacists, stock managers, and settlement staff."
              className="min-h-[132px] w-full rounded-[12px] bg-[rgb(var(--clr-surface))] px-4 py-3 text-body-medium text-on-surface outline-none shadow-[0px_0px_0px_1px_rgba(var(--clr-outline-variant),0.35)] placeholder:text-on-surface-var"
            />
          </div>
          {values.staffMembers.length > 0 ? (
            <div className="mt-4 overflow-x-auto rounded-[12px] bg-[rgb(var(--clr-surface))] shadow-[0px_0px_0px_1px_rgba(var(--clr-outline-variant),0.35)]">
              <table className="min-w-[820px] w-full">
                <thead>
                  <tr className="border-b border-[rgba(var(--clr-outline-variant),0.45)] text-left text-label-medium text-on-surface-var">
                    <th className="px-4 py-3 font-medium">Staff member</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Branch</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {values.staffMembers.slice(0, 8).map((member) => (
                    <tr key={member.id} className="border-b border-[rgba(var(--clr-outline-variant),0.45)] last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="text-body-medium text-on-surface">{member.fullName || "—"}</div>
                        <div className="text-body-small text-on-surface-var">{member.staffNumber || member.dob || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-body-medium text-on-surface-var">{member.role || "—"}</td>
                      <td className="px-4 py-3 text-body-medium text-on-surface-var">{member.branch || "—"}</td>
                      <td className="px-4 py-3 text-body-medium text-on-surface-var">{member.email || "—"}</td>
                      <td className="px-4 py-3 text-body-medium text-on-surface-var">{member.phone || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {values.staffMembers.length > 8 ? (
                <div className="border-t border-[rgba(var(--clr-outline-variant),0.45)] px-4 py-3 text-body-small text-on-surface-var">
                  Showing the first 8 staff records. The remaining {values.staffMembers.length - 8} will still carry forward into onboarding state.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </Shell>
  );
}

function SummaryRow({ label, value, first = false }) {
  return (
    <div
      className={[
        "relative flex items-start justify-between gap-4 px-4 py-3",
        first ? "" : "before:absolute before:left-4 before:right-0 before:top-0 before:h-px before:bg-[rgba(var(--clr-outline-variant),0.45)]",
      ].join(" ")}
    >
      <div className="text-body-medium text-on-surface-var">{label}</div>
      <div className="text-body-medium text-on-surface text-right">{value || "—"}</div>
    </div>
  );
}

function StepReview({
  step,
  total,
  values,
  onBack,
  onFinish,
  finishing = false,
  connectState = {},
  stagingBypassEligible = false,
}) {
  const serviceModes = [values.pickupEnabled ? "Pickup" : null, values.deliveryEnabled ? "Delivery" : null]
    .filter(Boolean)
    .join(" + ");
  const responsibleLead =
    (!isEmailHandleLike(values.adminName) ? String(values.adminName || "").trim() : "") ||
    [values.firstName, values.lastName].filter(Boolean).join(" ").trim();
  const staffMagicLinkUrl = createPharmacyInviteSignInUrl("", DASHBOARD_PATH);
  const onboardingState = String(connectState?.onboardingState || "").trim();
  const paymentState = String(connectState?.paymentState || "").trim();
  const connectButtonLabel = finishing
    ? stagingBypassEligible
      ? "Opening Pharmacy dashboard..."
      : "Checking Stripe payout readiness..."
    : onboardingState === "onboarded" || stagingBypassEligible
    ? "Launch Pharmacy dashboard"
    : "Continue to Stripe Connect";
  const connectSummary =
    onboardingState === "onboarded"
      ? "Pharmacy payout ownership is connected and ready. AfriMed™ can safely route sponsor and prescription payments to this pharmacy account."
      : stagingBypassEligible
      ? "This invited tester account can enter the Pharmacy dashboard in staging while pharmacy verification and payout readiness remain pending. This is not production approval."
      : paymentState === "payments_blocked"
      ? "Pharmacy dashboard launch is blocked until Stripe Connect is completed for this branch payout account."
      : "Stripe Connect must be completed before this pharmacy can be treated as payout-ready.";

  return (
    <Shell
      step={step}
      total={total}
      onBack={onBack}
      title="Launch the Pharmacy dashboard"
      subtitle="Review your staging pharmacy profile before opening the dashboard. Verification and payout readiness can remain pending for invited testers."
      footer={
        <Button fullWidth disabled={finishing} onClick={onFinish}>
          {connectButtonLabel}
        </Button>
      }
    >
      <div className="grid gap-4">
        <div className="rounded-[12px] bg-[rgb(var(--clr-surface))] py-1 shadow-[0px_0px_0px_1px_rgba(var(--clr-outline-variant),0.35)]">
          <SummaryRow first label="Pharmacy" value={values.pharmacyName} />
          <SummaryRow label="Branch type" value={values.branchType} />
          <SummaryRow label="Location" value={[values.city, values.country].filter(Boolean).join(", ")} />
          <SummaryRow
            label="Branch geodata"
            value={
              values.latitude && values.longitude
                ? `${values.latitude}, ${values.longitude}`
                : "Not captured yet"
            }
          />
          <SummaryRow label="Responsible lead" value={responsibleLead} />
          <SummaryRow label="Fulfilment" value={serviceModes} />
          <SummaryRow
            label="Delivery policy"
            value={
              values.deliveryEnabled
                ? `${values.deliveryRadiusKm || "—"} km radius • fee ${values.deliveryBaseFee || "—"}`
                : "Delivery disabled"
            }
          />
          <SummaryRow
            label="Verification"
            value={
              values.verificationDeferred
                ? "Pending - verify later"
                : `${values.uploadNames.pharmacyLicense.length + values.uploadNames.pharmacistRegistration.length + values.uploadNames.proofOfAddress.length + values.uploadNames.deliveryPermit.length} files prepared`
            }
          />
          <SummaryRow label="Imported staff" value={values.staffMembers.length ? `${values.staffMembers.length} records prepared` : "Manual team setup"} />
          <SummaryRow label="Imported inventory" value={values.inventoryItems.length ? `${values.inventoryItems.length} stock rows prepared` : "Manual stock setup"} />
        </div>

        <div className={TERTIARY_INFO_PANEL_CLASS}>
          <div className="text-body-large text-on-surface">What happens next</div>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-body-medium text-on-surface-var">
            <li>Use the dashboard to explore staging workflows while verification and payout readiness remain pending.</li>
            <li>{connectSummary}</li>
            <li>Pending verification does not mean production approval.</li>
            <li>Live prescription routing, paid fulfilment, payout settlement, and public branch availability require verification and admin approval later.</li>
            <li>The Pharmacy dashboard opens against your saved branch profile, staff list, and inventory rows once you add them.</li>
            <li>
              Added or imported staff must use their email address to request a secure sign-in link before accessing the AfriMed™ Pharmacy dashboard. Provide them with this link{" "}
              <a
                href={staffMagicLinkUrl}
                className="text-primary underline underline-offset-4"
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
              .
            </li>
          </ul>
          {connectState?.error ? (
            <div className="mt-4 rounded-[12px] border border-[rgba(220,38,38,0.28)] bg-[rgba(220,38,38,0.08)] p-3 text-body-small text-on-surface">
              {connectState.error}
            </div>
          ) : null}
        </div>
      </div>
    </Shell>
  );
}

export default function PharmacyOnboarding({ backToCategory }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode =
    new URLSearchParams(location.search || "").get("edit") === "1";
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const restored = React.useMemo(
    () => mergeState(loadProgress(LS_KEY) || {}),
    [],
  );
  const [step, setStep] = React.useState(0);
  const [values, setValues] = React.useState(restored);
  const [finishing, setFinishing] = React.useState(false);
  const [connectState, setConnectState] = React.useState({
    onboardingState: "onboarding_incomplete",
    paymentState: "payments_blocked",
    error: "",
  });
  const [stagingBypassEligible, setStagingBypassEligible] = React.useState(false);
  const [stagingTesterEmail, setStagingTesterEmail] = React.useState("");
  const pharmacyIdentityStartedRef = React.useRef(false);
  const resolvedAuthEmail = normalizeStagingEmail(
    auth?.user?.email || auth?.profile?.email || "",
  );
  const authMetadata = {
    ...(auth?.user?.app_metadata || {}),
    ...(auth?.user?.user_metadata || {}),
    ...(auth?.profile || {}),
  };
  const isStagingRuntime = isStagingPharmacyBypassRuntime();
  const isSmokeOrDemoStagingAccount =
    isStagingRuntime &&
    (hasStagingSmokeMetadata(authMetadata) || isStagingSmokeEmail(resolvedAuthEmail));
  const canBypassPharmacyOnboardingRequirements =
    isStagingRuntime || stagingBypassEligible || isSmokeOrDemoStagingAccount;
  const orientationStorageKey = React.useMemo(
    () => getPharmacyOrientationKey(auth?.user?.id, resolvedAuthEmail),
    [auth?.user?.id, resolvedAuthEmail],
  );
  const [orientationOpen, setOrientationOpen] = React.useState(false);
  const orientationFirstName = React.useMemo(
    () => getFirstTokenName(values.firstName) || getFirstNameFromAuth(auth),
    [auth, values.firstName],
  );
  const [memberOnboardingGuard, setMemberOnboardingGuard] = React.useState({
    checking: Boolean(auth?.user?.id),
    redirecting: false,
  });

  React.useEffect(() => {
    let alive = true;
    const userId = String(auth?.user?.id || "").trim();
    const email = normalizeStagingEmail(resolvedAuthEmail || auth?.user?.email || "");

    if (!userId) {
      setMemberOnboardingGuard({ checking: false, redirecting: false });
      return () => {
        alive = false;
      };
    }

    setMemberOnboardingGuard({ checking: true, redirecting: false });

    async function redirectMemberAwayFromOwnerOnboarding() {
      if (isEditMode) {
        setMemberOnboardingGuard({ checking: false, redirecting: false });
        return;
      }
      try {
        const access = await resolvePersistedPharmacyAccess({ userId, email });
        const memberAccess = getActivePharmacyMemberAccess(access, { userId, email });
        if (!alive) return;
        if (memberAccess) {
          writeLastRole("pharmacy", userId);
          writeLastAuthedPath(DASHBOARD_PATH, userId);
          setMemberOnboardingGuard({ checking: false, redirecting: true });
          navigate(DASHBOARD_PATH, {
            replace: true,
            state: {
              pharmacyMember: true,
              ownerUserId: memberAccess?.owner_user_id || "",
              memberRole: memberAccess?.role || "",
            },
          });
          return;
        }
      } catch (error) {
        console.warn("[PharmacyOnboarding] pharmacy member redirect skipped:", error?.message || error);
      }
      if (alive) {
        setMemberOnboardingGuard({ checking: false, redirecting: false });
      }
    }

    redirectMemberAwayFromOwnerOnboarding();
    return () => {
      alive = false;
    };
  }, [auth?.user?.email, auth?.user?.id, isEditMode, navigate, resolvedAuthEmail]);

  React.useEffect(() => {
    if (memberOnboardingGuard.checking || memberOnboardingGuard.redirecting) return;
    if (step !== 0 || typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(orientationStorageKey) === "1") return;
    } catch {
      // If storage is unavailable, showing the dialog once per mount is acceptable.
    }
    setOrientationOpen(true);
  }, [memberOnboardingGuard.checking, memberOnboardingGuard.redirecting, orientationStorageKey, step]);

  const dismissOrientation = React.useCallback(() => {
    try {
      sessionStorage.setItem(orientationStorageKey, "1");
    } catch {
      // ignore storage failures
    }
    setOrientationOpen(false);
  }, [orientationStorageKey]);

  const recordPharmacyTruthEvent = React.useCallback(
    async (eventType, { route = "/onboarding/pharmacy", stepName = eventType, metadata = {} } = {}) => {
      const userId = String(auth?.user?.id || "").trim();
      if (!userId) return;
      const eventMetadata = {
        lane: "pharmacy",
        pharmacy: true,
        ...(metadata && typeof metadata === "object" ? metadata : {}),
      };
      const writes = [
        writeOnboardingEvent({
          userId,
          role: "pharmacy",
          step: stepName,
          eventType,
          meta: eventMetadata,
        }),
        writeUserActivityEvent({
          userId,
          role: "pharmacy",
          route,
          eventType,
          metadata: eventMetadata,
        }),
      ];
      const results = await Promise.allSettled(writes);
      results.forEach((result) => {
        if (result.status === "rejected") {
          console.warn("[AfriMed pharmacy] truth event skipped:", result.reason?.message || result.reason);
        }
      });
    },
    [auth?.user?.id],
  );

  React.useEffect(() => {
    saveProgress(LS_KEY, values);
  }, [values]);

  React.useEffect(() => {
    if (memberOnboardingGuard.checking || memberOnboardingGuard.redirecting) return;
    if (!auth?.user?.id) return;
    const stepNumber = Number(step || 0) + 1;
    void recordPharmacyTruthEvent(step === 0 ? "pharmacy_onboarding_started" : "pharmacy_steps_continued", {
      stepName: `pharmacy_step_${stepNumber}_of_5`,
      metadata: {
        step_index: step,
        step_number: stepNumber,
        total_steps: 5,
      },
    });
  }, [
    auth?.user?.id,
    memberOnboardingGuard.checking,
    memberOnboardingGuard.redirecting,
    recordPharmacyTruthEvent,
    step,
  ]);

  React.useEffect(() => {
    if (!canBypassPharmacyOnboardingRequirements) return;
    const email = normalizeStagingEmail(stagingTesterEmail || resolvedAuthEmail || auth?.user?.email || "");
    const displayName = String(
      auth?.profile?.full_name ||
        auth?.profile?.display_name ||
        auth?.user?.user_metadata?.full_name ||
        auth?.user?.user_metadata?.name ||
        "",
    ).trim();
    const patch = {};
    const authFirstName = String(
      auth?.profile?.first_name || auth?.user?.user_metadata?.first_name || "",
    ).trim();
    const authLastName = String(
      auth?.profile?.last_name || auth?.user?.user_metadata?.last_name || "",
    ).trim();
    if (!String(values.firstName || "").trim() && authFirstName) {
      patch.firstName = authFirstName;
    }
    if (!String(values.lastName || "").trim() && authLastName) {
      patch.lastName = authLastName;
    }
    if (!String(values.adminName || "").trim() || isEmailHandleLike(values.adminName)) {
      patch.adminName =
        [authFirstName, authLastName].filter(Boolean).join(" ").trim() ||
        (!isEmailHandleLike(displayName) ? displayName : "") ||
        "Staging pharmacy owner";
    }
    if (!String(values.adminEmail || "").trim() && email) {
      patch.adminEmail = email;
    }
    if (Object.keys(patch).length > 0) {
      setValues((current) => ({ ...current, ...patch }));
    }
  }, [
    auth?.profile?.display_name,
    auth?.profile?.first_name,
    auth?.profile?.full_name,
    auth?.profile?.last_name,
    auth?.user?.email,
    auth?.user?.user_metadata?.first_name,
    auth?.user?.user_metadata?.full_name,
    auth?.user?.user_metadata?.last_name,
    auth?.user?.user_metadata?.name,
    canBypassPharmacyOnboardingRequirements,
    resolvedAuthEmail,
    stagingTesterEmail,
    values.adminEmail,
    values.adminName,
    values.firstName,
    values.lastName,
  ]);

  React.useEffect(() => {
    let alive = true;

    async function resolveStagingBypass() {
      if (typeof window === "undefined") return;
      if (!isStagingRuntimeHost(window.location.hostname) && import.meta.env.DEV !== true) return;
      let lookupEmail = resolvedAuthEmail;
      let authUser = auth?.user || null;

      if (!lookupEmail || !authUser?.id) {
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          authUser = user || authUser;
          lookupEmail = normalizeStagingEmail(lookupEmail || user?.email || "");
        } catch {
          lookupEmail = resolvedAuthEmail;
        }
      }

      const metadata = {
        ...(authUser?.app_metadata || {}),
        ...(authUser?.user_metadata || {}),
        ...(auth?.profile || {}),
      };
      const isSmokeOrDemo =
        metadata?.smoke_account === true ||
        metadata?.pharmacy_smoke === true ||
        metadata?.is_demo === true ||
        String(metadata?.account_classification || "").toLowerCase() === "smoke";

      if (isSmokeOrDemo) {
        if (!alive) return;
        setStagingBypassEligible(true);
        setStagingTesterEmail(lookupEmail || resolvedAuthEmail);
        return;
      }

      if (!lookupEmail) return;

      const cachedAccess = readCachedStagingAccess();
      if (cachedAccess?.email === lookupEmail) {
        if (!alive) return;
        setStagingBypassEligible(true);
        setStagingTesterEmail(lookupEmail);
        return;
      }

      try {
        const access = await checkStagingAccess(lookupEmail);
        if (!alive || !access?.allowed) return;
        setStagingBypassEligible(true);
        setStagingTesterEmail(access.email || lookupEmail);
      } catch (stagingError) {
        console.warn("[PharmacyOnboarding] staging bypass lookup skipped:", stagingError);
      }
    }

    resolveStagingBypass();
    return () => {
      alive = false;
    };
  }, [resolvedAuthEmail]);

  React.useEffect(() => {
    const userId = String(auth?.user?.id || "").trim();
    if (memberOnboardingGuard.checking || memberOnboardingGuard.redirecting) return;
    if (!userId || pharmacyIdentityStartedRef.current) return;
    pharmacyIdentityStartedRef.current = true;

    async function ensurePharmacyIdentityStarted() {
      let roleHint = "pharmacy";
      if (resolvedAuthEmail) {
        try {
          const { data } = await supabase
            .from("staging_access_invites")
            .select("role_hint")
            .eq("email", resolvedAuthEmail)
            .maybeSingle();
          roleHint = data?.role_hint || roleHint;
        } catch {
          roleHint = "pharmacy";
        }
      }

      await persistPharmacyOnboardingIdentity({
        userId,
        email: resolvedAuthEmail,
        roleHint,
        source: "pharmacy_onboarding_mount",
        nextRoute: "/onboarding/pharmacy",
        eventName: "pharmacy_onboarding_started",
      });
    }

    ensurePharmacyIdentityStarted().catch((error) => {
      pharmacyIdentityStartedRef.current = false;
      console.warn("[PharmacyOnboarding] pharmacy identity start skipped:", error?.message || error);
    });
  }, [
    auth?.user?.id,
    memberOnboardingGuard.checking,
    memberOnboardingGuard.redirecting,
    resolvedAuthEmail,
  ]);

  const update = React.useCallback((patch) => {
    setValues((current) => ({ ...current, ...patch }));
  }, []);

  const handleUpload = React.useCallback((key, files = []) => {
    const nextNames = Array.isArray(files)
      ? files.map((file) => String(file?.name || "").trim()).filter(Boolean)
      : [];
    setValues((current) => ({
      ...current,
      uploadNames: {
        ...current.uploadNames,
        [key]: nextNames,
      },
      verificationDeferred: false,
      verificationStatus: "pending",
    }));
  }, []);

  const deferVerification = React.useCallback(() => {
    setValues((current) => ({
      ...current,
      verificationDeferred: true,
      verificationDeferredAt: new Date().toISOString(),
      verificationDeferredBy: stagingTesterEmail || resolvedAuthEmail || "",
      verificationStatus: "pending",
      uploadNames: {
        ...current.uploadNames,
        pharmacyLicense: [],
        pharmacistRegistration: [],
        proofOfAddress: [],
        deliveryPermit: current.uploadNames.deliveryPermit || [],
      },
    }));
    window.setTimeout(() => {
      setStep(3);
    }, 0);
  }, [resolvedAuthEmail, stagingTesterEmail]);

  const totalSteps = 5;
  const goBack = () => {
    if (finishing) return;
    if (step === 0) {
      backToCategory?.();
      return;
    }
    setStep((current) => Math.max(0, current - 1));
  };

  const ensureCanManageOwnerProfile = React.useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const userId = String(user?.id || "").trim();
    const email = String(user?.email || "").trim().toLowerCase();

    if (!userId) {
      throw new Error("Sign in again before continuing with pharmacy onboarding.");
    }

    const access = await resolvePersistedPharmacyAccess({
      userId,
      email,
    }).catch(() => ({
      allowed: false,
      hasOwnerProfile: false,
      rows: [],
    }));

    if (getActivePharmacyMemberAccess(access, { userId, email })) {
      throw new Error(MEMBER_ONLY_OWNER_ERROR);
    }

    return {
      userId,
    };
  }, []);

  const persistPharmacySnapshot = React.useCallback(async (snapshot) => {
    const nextSnapshot = {
      ...snapshot,
      completedAt: snapshot?.completedAt || new Date().toISOString(),
    };
    const persistedProfile = savePharmacyOnboardingTruth(nextSnapshot, {});
    try {
      localStorage.setItem("afrimed:pharmacy:profile:v1", JSON.stringify(persistedProfile));
    } catch {
      // ignore storage failures
    }
    saveProgress(LS_KEY, persistedProfile);
    try {
      const { userId } = await ensureCanManageOwnerProfile();
      const cleanFirstName = String(persistedProfile.firstName || "").trim();
      const cleanLastName = String(persistedProfile.lastName || "").trim();
      const cleanFullName = [cleanFirstName, cleanLastName].filter(Boolean).join(" ").trim();
      if (cleanFullName) {
        await supabase.auth.updateUser({
          data: {
            ...(auth?.user?.user_metadata || {}),
            role: "pharmacy",
            primary_role: "pharmacy",
            first_name: cleanFirstName || auth?.user?.user_metadata?.first_name || "",
            last_name: cleanLastName || auth?.user?.user_metadata?.last_name || "",
            full_name: cleanFullName,
            display_name: cleanFullName,
          },
        }).catch((error) => {
          console.warn("[AfriMed pharmacy] Identity metadata sync skipped:", error?.message || error);
        });
      }
      const { error } = await supabase.from("pharmacy_connect_profiles").upsert(
        {
          user_id: userId,
          pharmacy_name: persistedProfile.pharmacyName || null,
          trading_name: persistedProfile.tradingName || null,
          country: persistedProfile.country || null,
          connect_meta: buildConnectMeta(persistedProfile),
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" },
      );
      if (error) throw error;
    } catch (error) {
      if (nextSnapshot.stagingDashboardAccess === true || nextSnapshot.stagingOnboardingBypass === true) {
        throw error;
      }
      // Keep onboarding progress local even if shared profile sync fails.
    }
    return persistedProfile;
  }, [auth?.user?.user_metadata, ensureCanManageOwnerProfile]);

  const finalizeOnboarding = React.useCallback((snapshot, status) => {
    const nextProfile = savePharmacyOnboardingTruth(snapshot, status);
    saveProgress(LS_KEY, nextProfile);
    navigate(DASHBOARD_PATH, { replace: true });
  }, [navigate]);

  const finish = React.useCallback(async () => {
    if (finishing) return;
    setFinishing(true);
    setConnectState((current) => ({
      ...current,
      error: "",
    }));

    try {
      await ensureCanManageOwnerProfile();
      const shouldUseStagingDashboardBypass =
        canBypassPharmacyOnboardingRequirements ||
        isStagingPharmacyBypassRuntime();
      const snapshotForPersistence = shouldUseStagingDashboardBypass
        ? applyStagingPharmacyPlaceholders(values, {
            email: stagingTesterEmail || resolvedAuthEmail || auth?.user?.email || "",
          })
        : values;
      const persistedSnapshot = await persistPharmacySnapshot({
        ...snapshotForPersistence,
        completedAt: new Date().toISOString(),
      });

      if (shouldUseStagingDashboardBypass) {
        const launchedUserId = String(auth?.user?.id || "").trim();
        const stagingSnapshot = savePharmacyOnboardingTruth(
          {
            ...persistedSnapshot,
            stagingDashboardAccess: true,
            stagingDashboardAccessAt: new Date().toISOString(),
            stagingOnboardingBypass: true,
            branchProfileIncomplete: true,
            payoutPending: true,
            verificationDeferred: true,
            verificationStatus: "pending",
          },
          {},
        );
        saveProgress(LS_KEY, stagingSnapshot);
        if (launchedUserId) {
          writeLastRole("pharmacy", launchedUserId);
          writeLastAuthedPath(DASHBOARD_PATH, launchedUserId);
          const ownerProfileIsReadable = await confirmPersistedPharmacyOwnerProfile(launchedUserId).catch((error) => {
            console.warn("[AfriMed pharmacy] Owner profile readback skipped:", error?.message || error);
            return false;
          });
          await recordPharmacyTruthEvent("pharmacy_dashboard_launched", {
            route: DASHBOARD_PATH,
            stepName: "pharmacy_dashboard_launched",
            metadata: {
              dashboard_reached: true,
              staging_dashboard_access: true,
              owner_profile_readable: ownerProfileIsReadable,
            },
          });
          const seedResult = await seedStagingPharmacyDemoData({ ownerUserId: launchedUserId }).catch((error) => {
            if (typeof console !== "undefined") {
              console.warn("[AfriMed pharmacy] Demo data seed skipped", {
                ownerUserId: launchedUserId,
                reason: error?.message || "unknown",
              });
            }
            return null;
          });
          if (Array.isArray(seedResult?.inventoryPreview) && seedResult.inventoryPreview.length) {
            const seededSnapshot = savePharmacyOnboardingTruth(
              {
                ...stagingSnapshot,
                inventoryItems: seedResult.inventoryPreview,
                inventoryCsvNames: ["AfriMed staging demo stock"],
              },
              {},
            );
            saveProgress(LS_KEY, seededSnapshot);
          }
        }
        await recordPharmacyTruthEvent("pharmacy_dashboard_reached", {
          route: DASHBOARD_PATH,
          stepName: "pharmacy_dashboard_reached",
          metadata: {
            dashboard_reached: true,
            staging_dashboard_access: true,
          },
        });
        navigate(DASHBOARD_PATH, { replace: true });
        return;
      }

      const status = await getPharmacyStripeConnectStatus().catch(() => ({}));
      const truth = derivePharmacyOnboardingTruth(persistedSnapshot, status);

      setConnectState({
        ...truth,
        error: "",
      });

      if (truth.ownershipReady) {
        await recordPharmacyTruthEvent("pharmacy_dashboard_launched", {
          route: DASHBOARD_PATH,
          stepName: "pharmacy_dashboard_launched",
          metadata: {
            dashboard_reached: true,
            stripe_ready: true,
          },
        });
        finalizeOnboarding(persistedSnapshot, status);
        return;
      }

      const next = await startPharmacyStripeConnect({
        refreshPath: "/onboarding/pharmacy?stripe=refresh",
        returnPath: "/onboarding/pharmacy?stripe=return",
        testingSetup: import.meta.env.DEV === true,
      });
      const onboardingUrl = String(next?.onboarding_url || "").trim();
      if (!onboardingUrl) {
        throw new Error("Stripe Connect onboarding could not be started right now.");
      }
      window.location.assign(onboardingUrl);
    } catch (error) {
      setConnectState((current) => ({
        ...current,
        onboardingState: "onboarding_incomplete",
        paymentState: "payments_blocked",
          error:
          error?.message ||
          "We could not confirm the pharmacy payout account yet. Finish Stripe Connect before AfriMed™ treats this branch as onboarded.",
      }));
      setFinishing(false);
    }
  }, [
    auth?.profile,
    auth?.user,
    canBypassPharmacyOnboardingRequirements,
    finalizeOnboarding,
    finishing,
    navigate,
    persistPharmacySnapshot,
    recordPharmacyTruthEvent,
    resolvedAuthEmail,
    stagingTesterEmail,
    values,
  ]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const stripeState = String(params.get("stripe") || "").trim().toLowerCase();
    if (stripeState !== "return" && stripeState !== "refresh") return undefined;

    let alive = true;
    setStep(4);
    setFinishing(true);

    async function syncStripeReturn() {
      try {
        const persistedSnapshot = await persistPharmacySnapshot(values);
        const status = await getPharmacyStripeConnectStatus();
        const truth = derivePharmacyOnboardingTruth(persistedSnapshot, status);
        if (!alive) return;

        setConnectState({
          ...truth,
          error: truth.ownershipReady
            ? ""
            : "Stripe Connect is not payout-ready yet. Complete the required Stripe details before AfriMed™ can activate pharmacy settlements.",
        });

        if (truth.ownershipReady) {
          finalizeOnboarding(persistedSnapshot, status);
          return;
        }
      } catch (error) {
        if (!alive) return;
        setConnectState({
          onboardingState: "onboarding_incomplete",
          paymentState: "payments_blocked",
          error:
            error?.message ||
            "We could not confirm the pharmacy payout account after Stripe returned.",
        });
      } finally {
        if (alive) setFinishing(false);
      }
    }

    syncStripeReturn();
    return () => {
      alive = false;
    };
  }, [finalizeOnboarding, location.search, persistPharmacySnapshot, values]);

  const withOrientation = (node) => (
    <>
      <PharmacyOrientationDialog
        open={step === 0 && orientationOpen}
        firstName={orientationFirstName}
        onDismiss={dismissOrientation}
      />
      {node}
    </>
  );

  if (memberOnboardingGuard.checking || memberOnboardingGuard.redirecting) {
    return <RouteFallback label="Opening Pharmacy..." />;
  }

  if (step === 0) {
    return withOrientation(
      <StepIdentity
        step={1}
        total={totalSteps}
        values={values}
        onChange={update}
        onBack={goBack}
        onContinue={() => setStep(1)}
        canBypassPharmacyOnboardingRequirements={canBypassPharmacyOnboardingRequirements}
      />
    );
  }

  if (step === 1) {
    return withOrientation(
      <StepOperations
        step={2}
        total={totalSteps}
        values={values}
        onChange={update}
        onBack={goBack}
        onContinue={() => setStep(2)}
        canBypassPharmacyOnboardingRequirements={canBypassPharmacyOnboardingRequirements}
      />
    );
  }

  if (step === 2) {
    return withOrientation(
      <StepVerification
        step={3}
        total={totalSteps}
        values={values}
        onUpload={handleUpload}
        onBack={goBack}
        onContinue={() => setStep(3)}
        onDefer={deferVerification}
        stagingBypassEligible={stagingBypassEligible}
        stagingTesterEmail={stagingTesterEmail}
      />
    );
  }

  if (step === 3) {
    return withOrientation(
      <StepTeam
        step={4}
        total={totalSteps}
        values={values}
        onChange={update}
        onBack={goBack}
        onContinue={() => setStep(4)}
        canBypassPharmacyOnboardingRequirements={canBypassPharmacyOnboardingRequirements}
      />
    );
  }

  return withOrientation(
    <StepReview
      step={5}
      total={totalSteps}
      values={values}
      onBack={goBack}
      onFinish={finish}
      finishing={finishing}
      connectState={connectState}
      stagingBypassEligible={canBypassPharmacyOnboardingRequirements}
    />
  );
}
